import React, { useState, useMemo } from 'react';
import { useAvailability } from '../hooks/useAvailability';
import Calendar from '../components/Calendar';

const DAYS_NL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

export default function AdminCalendarPage() {
  const { availability, isConnected, isLoading, updateAvailability } = useAvailability();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<'available' | 'unavailable' | 'partial'>('available');
  const [editNotes, setEditNotes] = useState('');
  const [editTimeSlots, setEditTimeSlots] = useState([{ start: '09:00', end: '17:00' }]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  // When selecting a date, load its data
  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    const data = availability[date];
    if (data) {
      setEditStatus(data.status);
      setEditNotes(data.notes || '');
      setEditTimeSlots(data.timeSlots || [{ start: '09:00', end: '17:00' }]);
    } else {
      setEditStatus('available');
      setEditNotes('');
      setEditTimeSlots([{ start: '09:00', end: '17:00' }]);
    }
    setMessage('');
  };

  const handleSave = async () => {
    if (!selectedDate) return;
    
    setIsSaving(true);
    setMessage('');
    
    try {
      await updateAvailability(
        selectedDate,
        editStatus,
        editNotes,
        editStatus !== 'unavailable' ? editTimeSlots : undefined
      );
      setMessage('✅ Opgeslagen!');
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const selectedDateFormatted = useMemo(() => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate);
    return `${DAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]} ${d.getFullYear()}`;
  }, [selectedDate]);

  const addTimeSlot = () => {
    setEditTimeSlots([...editTimeSlots, { start: '09:00', end: '17:00' }]);
  };

  const removeTimeSlot = (index: number) => {
    setEditTimeSlots(editTimeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index: number, field: 'start' | 'end', value: string) => {
    const updated = [...editTimeSlots];
    updated[index][field] = value;
    setEditTimeSlots(updated);
  };

  if (isLoading) {
    return (
      <div className="container" style={{ textAlign: 'center', marginTop: '50px' }}>
        Beschikbaarheid laden...
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Beheer je Beschikbaarheid</h2>
        <div className={`realtime-indicator ${isConnected ? '' : 'disconnected'}`}>
          <div className="realtime-dot"></div>
          {isConnected ? 'Live verbonden' : 'Verbinding verbroken'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
        <div>
          <Calendar
            availability={availability}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
          
          <div className="legend">
            <div className="legend-item">
              <div className="legend-dot available"></div>
              Beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot unavailable"></div>
              Niet beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot partial"></div>
              Gedeeltelijk
            </div>
          </div>
        </div>

        {selectedDate && (
          <div className="day-detail">
            <h3>{selectedDateFormatted}</h3>

            <div className="status-buttons">
              <button
                className={`status-btn ${editStatus === 'available' ? 'available' : ''}`}
                onClick={() => setEditStatus('available')}
              >
                ✅ Beschikbaar
              </button>
              <button
                className={`status-btn ${editStatus === 'partial' ? 'active' : ''}`}
                onClick={() => setEditStatus('partial')}
              >
                🕐 Gedeeltelijk
              </button>
              <button
                className={`status-btn ${editStatus === 'unavailable' ? 'unavailable' : ''}`}
                onClick={() => setEditStatus('unavailable')}
              >
                ❌ Niet beschikbaar
              </button>
            </div>

            {editStatus !== 'unavailable' && (
              <div className="time-slots">
                <label style={{ fontWeight: 500, marginBottom: 10, display: 'block' }}>
                  Beschikbare tijden:
                </label>
                {editTimeSlots.map((slot, index) => (
                  <div className="time-slot" key={index}>
                    <input
                      type="time"
                      value={slot.start}
                      onChange={(e) => updateTimeSlot(index, 'start', e.target.value)}
                    />
                    <span>tot</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={(e) => updateTimeSlot(index, 'end', e.target.value)}
                    />
                    {editTimeSlots.length > 1 && (
                      <button
                        onClick={() => removeTimeSlot(index)}
                        style={{ background: '#ffebee', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addTimeSlot}
                  style={{ marginTop: 10, background: '#e3f2fd', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
                >
                  + Tijdslot toevoegen
                </button>
              </div>
            )}

            <textarea
              className="notes-input"
              placeholder="Notities (optioneel)"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
              style={{ marginTop: 20 }}
            >
              {isSaving ? 'Opslaan...' : 'Opslaan'}
            </button>

            {message && (
              <div style={{ marginTop: 15, textAlign: 'center' }}>{message}</div>
            )}
          </div>
        )}

        {!selectedDate && (
          <div className="day-detail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
            <p>Selecteer een datum om de beschikbaarheid te bewerken</p>
          </div>
        )}
      </div>
    </div>
  );
}
